import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useLoaderData, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import "./BerthDetail.css";
import { Typography, Box, Card, CardContent } from "@mui/material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import { RequestTimeline, RejectionModal } from "../../../../../components";
import { getBerthById, updateBerthStatus } from "../../../../../services";
import defaultBlogImage from "../../../../../assets/default-blog.png";
import { createPayment } from "../../../../../services/payment";
import { formatEuroCurrency } from "../../../../../utils/currency";

export const BerthDetail = () => {
  const baseUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const user = useLoaderData();
  const { type, value } = useLocation()?.state;
  // console.log("location: ", value);
  const [loading, setLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [updateType, setUpdateType] = useState("");
  const [berth, setBerth] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);

  const {
    id,
    currentStatus,
    transactionType,
    destinationUserYacthname,
    createdAt,
    statusHistory,
    totalAmount,
    amountDetails: { taxAmount, serviceCost, amount, taxRateApplied },
    bookingDate,
    sourceBerth: {
      id: berthId,
      portEmail,
      berthImagePath,
      name,
      currentLocation,
      power,
      berthSpecifications: { length, dockHeight, beam, depth },
    },
  } = value;

  const updateStatus = async (type, rejectionMessage = null) => {
    setLoading(true);
    const toastId = toast.loading("Updating berth status");
    setUpdateType(type);

    const res = await updateBerthStatus(type, id, user?.token, rejectionMessage);
    // console.log("update res: ", res);

    if (res?.status === true) {
      toast.success(`${type} successfully`, { id: toastId });
      setLoading(false);
      setShowRejectionModal(false); // Close modal if open
      navigate("/home");
      return;
    }

    if (res?.status === false) {
      if (res?.message === "Invalid or expired access token.") {
        toast.error("Token expired, please re-login and try", { id: toastId });
        setLoading(false);
        return;
      }

      toast.error(res?.message || "Something went wrong please try later", { id: toastId });
      setLoading(false);
      return;
    }

    toast.error("Something went wrong please try later", { id: toastId });
    setLoading(false);
    return;
  };

  const handleRejectionConfirm = (message) => {
    updateStatus("rejected", message);
  };

  const getBerth = async () => {
    const res = await getBerthById(berthId, user?.token);
    // console.log("berth res: ", res);

    if (res?.status === true) {
      setBerth(res?.data);
      return;
    }

    if (res?.status === false) {
      if (res?.message === "Invalid or expired access token.") {
        toast.error("Token expired, please re-login and try");
        setBerth(null);
        return;
      }

      toast.error("Something went wrong please try later");
      setBerth(null);
      return;
    }

    toast.error("Something went wrong please try later");
    setBerth(null);
    return;
  };

  useEffect(() => {
    if (type === "request") {
      getBerth();
    }
  }, []);

  // console.log("berth: ", berth);

  const handlePayment = async () => {
    setPayLoading(true);
    const res = await createPayment(user?.token, id);
    // console.log("payment res: ", res);

    if (res?.status === true) {
      setPayLoading(false);
      window.location.replace(res?.data?.url);
      return;
    }

    if (res?.status === false) {
      if (res.message === "Invalid or expired access token.") {
        toast.error("Token expired Please re-login and try");
        setPayLoading(false);
        return;
      }

      toast.error("Something went wrong, please try again later.");
      setPayLoading(false);
      return;
    }

    toast.error("Something went wrong, please try again later.");
    setPayLoading(false);
    return;
  };

  return (
    <Box>
      <Typography variant="h3" className="page-title">
        Berth Details
      </Typography>
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={8}>
              <div className="bearth-details-container" style={{ marginBottom: '15px' }}>
                <div className="bearth-content-row">
                  <div className="left-data">
                    <Typography sx={{ display: "flex" }} variant="h4" component="h4">
                      {name ? name : "--"}
                    </Typography>
                  </div>
                  <div className="right-data">
                    <div
                      className={`cardTypeBox ${
                        transactionType
                          ? transactionType === "hire or swap"
                            ? "hireOrSwap"
                            : transactionType
                          : "hireOrSwap"
                      }`}
                    >
                      Open to {transactionType ? transactionType : "--"}
                    </div>
                  </div>
                </div>
                <div className="bearth-content-row">
                  <div className="left-data">
                    <p>{currentLocation ? currentLocation?.name : "--"}</p>
                  </div>
                  <div className="right-data">
                    <p>Booking slot:{" "}
                    {bookingDate
                      ? dayjs(bookingDate.startDate).format("YYYY-MM-DD")
                      : "--"}{" "}
                    to{" "}
                    {bookingDate
                      ? dayjs(bookingDate.endDate).format("YYYY-MM-DD")
                      : "--"}</p>
                  </div>
                </div>
                {type === "request" && (
                <div className="bearth-content-row">
                  <div className="left-data">
                    <p>Request from{" "}<strong>{destinationUserYacthname ? destinationUserYacthname : "--"}</strong></p>
                  </div>
                  <div className="right-data">
                    <p>Berth's current availability slots:{" "}</p>
                  </div>
                </div>
                )}
                <div className="bearth-content-row" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                  {berth && berth?.availableDate?.map((item) => {
                    return (
                      <div className="berthTimeSlot">
                        <Typography variant="subtitle2">
                          {item?.startDate} to {item?.endDate}
                        </Typography>
                      </div>
                    );
                  })}{" "}
                </div>
              </div>

              <div className="CartItemContainer" style={{ marginBottom: '10px' }}>
                <div className="orderItems">
                  <div className="firstCartItemContainer">
                    <p>Order Amount</p>
                  </div>
                  <p>{formatEuroCurrency(amount || 0)}</p>
                </div>
                <div className="orderItems">
                  <div className="firstCartItemContainer">
                    <p>Tax{taxRateApplied ? ` (${taxRateApplied}%)` : ''}</p>
                  </div>
                  <p>{formatEuroCurrency(taxAmount || 0)}</p>
                </div>
                <div className="orderItems">
                  <div className="firstCartItemContainer">
                    <p>Service cost</p>
                  </div>
                  <p>{formatEuroCurrency(serviceCost || 0)}</p>
                </div>
                <div className="borderTop"></div>
                <div className="orderItems">
                  <div className="firstCartItemContainer">
                    <p>
                      <b>Sub Total</b>
                    </p>
                  </div>
                  <p>
                    <b>{formatEuroCurrency(totalAmount || 0)}</b>
                  </p>
                </div>
                <div className="borderTop"></div>
              </div>

              <div className="bearth-details-container">
                <Typography variant="h6" style={{ marginTop: '20px', fontWeight: 'bold', marginBottom: '15px' }}>
                  Berth Specifications:
                </Typography>
                <div className="bearth-content-row">
                  <div className="left-data">
                    <p>Length: <strong>{length ? length : "--"}m</strong></p>
                  </div>
                  <div className="right-data">
                    <p>Beam: <strong>{beam ? beam : "--"}m</strong></p>
                  </div>
                </div>
                <div className="bearth-content-row">
                  <div className="left-data">
                    <p>Depth: <strong>{depth ? depth : "--"}m</strong></p>
                  </div>
                  <div className="right-data">
                    <p>Power required: <strong>{power ? power : "--"}</strong></p>
                  </div>
                </div>
                <div className="bearth-content-row">
                  <div className="left-data">
                    <p>Dock height: <strong>{dockHeight ? dockHeight : "--"}m</strong></p>
                  </div>
                  <div className="right-data">
                    <p>Port email: <strong>{portEmail ? portEmail : "--"}</strong></p>
                  </div>
                </div>
              </div>

              <>
                <hr/>
                {type === "request" && (
                  <div className="btnContainer">
                    <Button
                      disabled={
                        currentStatus === "accepted" || currentStatus === "paid"
                      }
                      className="cancelBtn"
                      variant="contained"
                      onClick={() => setShowRejectionModal(true)}
                    >
                      Reject
                    </Button>
                    <Button
                      disabled={
                        currentStatus === "accepted" || currentStatus === "paid"
                      }
                      className="btn"
                      variant="contained"
                      loading={loading && updateType === "accepted"}
                      loadingPosition="end"
                      onClick={() => updateStatus("accepted")}
                    >
                      Accept
                    </Button>
                  </div>
                )}
                {type === "own_request" && (
                  <>
                    <div className="btnContainer">
                      <Button
                        disabled={currentStatus === "accepted" && transactionType.toLowerCase() !== "swap"}
                        className="cancelBtn"
                        variant="contained"
                        loading={loading && updateType === "cancelled"}
                        loadingPosition="end"
                        onClick={() => updateStatus("cancelled")}
                      >
                        Cancel
                      </Button>
                      {currentStatus === "paid" ? (
                        <Button
                          disabled={currentStatus !== "paid"}
                          className="btn"
                          variant="contained"
                          onClick={() => updateStatus("completed")}
                          loading={loading && updateType === "completed"}
                          loadingPosition="end"
                        >
                          complete {transactionType}
                        </Button>
                      ) : (
                        <Button
                          disabled={currentStatus !== "accepted"}
                          className="btn"
                          variant="contained"
                          onClick={
                            transactionType && transactionType.toLowerCase() === "swap" 
                            ? () => updateStatus("completed") 
                            : handlePayment
                          }
                          loading={payLoading}
                          loadingPosition="end"
                        >
                          {
                            transactionType && transactionType.toLowerCase() === "swap"
                            ? "Complete Swap"
                            : `Pay ${formatEuroCurrency(totalAmount)}`
                          }
                        </Button>
                      )}
                    </div>
                    {currentStatus === "paid" && (
                      <div className="completeTextContainer">
                        <Typography
                          variant="caption"
                          gutterBottom
                          sx={{ display: "block" }}
                        >
                          Only click the button above after a successful{" "}
                          {transactionType}
                        </Typography>
                      </div>
                    )}
                  </>
                )}
              </>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <div className="cartImageParent">
                <img
                  className="berthCartImage"
                  src={
                    berthImagePath
                      ? `${baseUrl}${berthImagePath}`
                      : defaultBlogImage
                  }
                  alt="berth-image"
                />
              </div>
              <RequestTimeline data={statusHistory} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <RejectionModal
        open={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onConfirm={handleRejectionConfirm}
        loading={loading && updateType === "rejected"}
      />
    </Box>
  );
};
